"use client";

import { useEffect, useState } from "react";
import { Card, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import WebService from "../../utility/WebService";
import CloseButton from "react-bootstrap/CloseButton";

type StatusType = "PENDING" | "RESOLVED" | "DELETED";

interface PlanInfo {
  _id: string;
  planName: string;
  amount: number;
  frequency: "MONTH" | "YEAR";
}

interface UserInfo {
  _id: string;
  name?: string;
  email?: string;
}

interface LocationCountry {
  _id: string;
  name: string;
  code?: string;
}

interface LocationState {
  _id: string;
  name: string;
}

interface LocationCity {
  _id: string;
  name: string;
}

interface MembershipRequestDetails {
  _id: string;
  userId?: UserInfo;
  planId?: PlanInfo;
  requestId?: string;

  // revenue
  companyLastYearRevenue?: number;
  company2ndLastYearRevenue?: number;
  company3ndLastYearRevenue?: number;

  referredBy?: string;

  // org
  OrginationName?: string;
  OrginationGST?: string;
  OrginationAddress?: string;
  Country?: LocationCountry;
  State?: LocationState;
  City?: LocationCity;
  OtherCityName?: string;
  zipCode?: string;
  headQuatersAt?: string;
  webSite?: string;
  focusedBusinessArea?: string;

  // primary contact
  primaryContactFullName?: string;
  primaryContactPosition?: string;
  primaryContactMobileNumber?: string;
  primaryContactEmail?: string;

  // secondary contact
  secondaryContactFullName?: string;
  secondaryContactPosition?: string;
  secondaryContactMobileNumber?: string;
  secondaryContactEmail?: string;

  // training contact
  trainingContactFullName?: string;
  trainingContactPosition?: string;
  trainingContactMobileNumber?: string;
  trainingContactEmail?: string;

  // marketing contact
  marketingContactFullName?: string;
  marketingContactPosition?: string;
  marketingContactMobileNumber?: string;
  marketingContactEmail?: string;

  status: StatusType;
  createdAt: string;
  resolvedComment?: string;
  resolvedDatetime?: string;
  resolvedBy?: {
    _id: string;
    name?: string;
    email?: string;
  };
}

const MembershipRequestDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<MembershipRequestDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [notFound, setNotFound] = useState<boolean>(false);

  const formatDateTime = (iso?: string) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleString();
  };

  const formatAmount = (v?: number) => {
    if (v === undefined || v === null) return "-";
    return `₹${(v / 100).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const badgeClass = (status: StatusType) => {
    switch (status) {
      case "PENDING":
        return "badge bg-warning text-dark";
      case "RESOLVED":
        return "badge bg-success";
      case "DELETED":
        return "badge bg-danger";
      default:
        return "badge bg-secondary";
    }
  };

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setNotFound(false);

    WebService.getAPI({
      action: `plans/membership-requests/${id}`,
      isShowError: true,
    })
      .then((res: any) => {

        if (!res?.data) {
          setNotFound(true);
          setData(null);
        } else {
          setData(res.data as MembershipRequestDetails);
        }
      })
      .catch((err: any) => {
        console.error("Error loading membership request details", err);
        setNotFound(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  return (
    <div className="events-main">
      <Card className="event-card">
        {/* Header: Back button on left + title */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          {/* <div className="d-flex align-items-center"> */}
          {/* <button
              type="button"
              className="btn btn-outline-secondary btn-sm me-3"
              onClick={() => navigate(-1)}
            >
              Back
            </button> */}
          <div className="page-title mb-0">Membership Request Details</div>

          <CloseButton onClick={() => navigate(-1)} />
          {/* </div> */}
        </div>

        {loading && (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" size="sm" className="me-2" />
            <span>Loading...</span>
          </div>
        )}

        {!loading && notFound && (
          <div className="alert alert-warning mb-0">
            Membership request not found.
          </div>
        )}

        {!loading && !notFound && data && (
          <div className="membership-details">
            {/* Step 1: Summary / Plan & Status */}
            <div className="mb-4">
              <h5 className="mb-3">
                {/* <span className="badge bg-primary me-2">Step 1</span> */}
                Plan & Status
              </h5>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Request ID:</strong> {data.requestId}
                </div>
                <div className="col-md-4">
                  <strong>Created At:</strong> {formatDateTime(data.createdAt)}
                </div>
                <div className="col-md-4">
                  <strong>Status:</strong>{" "}
                  <span className={badgeClass(data.status)}>
                    {data.status}
                  </span>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Plan:</strong>{" "}
                  {data.planId ? data.planId.planName : "-"}
                </div>
                <div className="col-md-4">
                  <strong>Plan Amount:</strong>{" "}
                  {data.planId ? formatAmount(data.planId.amount) : "-"}
                </div>
                {/* <div className="col-md-4">
                  <strong>Frequency:</strong>{" "}
                  {data.planId
                    ? data.planId.frequency === "YEAR"
                      ? "Yearly"
                      : "Monthly"
                    : "-"}
                </div> */}
              </div>

              <div className="row">
                {/* <div className="col-md-6">
                  <strong>User:</strong>{" "}
                  {data.userId?.name || "-"}
                </div> */}
                <div className="col-md-6">
                  <strong>User Email:</strong>{" "}
                  {data.userId?.email || "-"}
                </div>
              </div>
            </div>

            <hr />

            {/* Step 2: Revenue */}
            <div className="mb-4">
              <h5 className="mb-3">
                {/* <span className="badge bg-primary me-2">Step 2</span> */}
                Revenue
              </h5>

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Referred By:</strong>{" "}
                  {data.referredBy || "-"}
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <strong>Last Year Revenue:</strong>{" "}
                  {data.companyLastYearRevenue !== undefined &&
                    data.companyLastYearRevenue !== null
                    ? data.companyLastYearRevenue.toLocaleString("en-IN")
                    : "-"}
                </div>
                <div className="col-md-4">
                  <strong>2nd Last Year Revenue:</strong>{" "}
                  {data.company2ndLastYearRevenue !== undefined &&
                    data.company2ndLastYearRevenue !== null
                    ? data.company2ndLastYearRevenue.toLocaleString("en-IN")
                    : "-"}
                </div>
                <div className="col-md-4">
                  <strong>3rd Last Year Revenue:</strong>{" "}
                  {data.company3ndLastYearRevenue !== undefined &&
                    data.company3ndLastYearRevenue !== null
                    ? data.company3ndLastYearRevenue.toLocaleString("en-IN")
                    : "-"}
                </div>
              </div>
            </div>
            <hr />

            {/* Step 3: Organization */}
            <div className="mb-4">
              <h5 className="mb-3">
                {/* <span className="badge bg-primary me-2">Step 2</span> */}
                Organization
              </h5>

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Organization Name:</strong>{" "}
                  {data.OrginationName || "-"}
                </div>
                <div className="col-md-3">
                  <strong>GST Number:</strong>{" "}
                  {data.OrginationGST || "-"}
                </div>
                <div className="col-md-3">
                  <strong>Website:</strong>{" "}
                  {data.webSite ? (
                    <a
                      href={data.webSite}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {data.webSite}
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Address:</strong>{" "}
                  {data.OrginationAddress || "-"}
                </div>
                <div className="col-md-6">
                  <strong>Headquarters At:</strong>{" "}
                  {data.headQuatersAt || "-"}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-3">
                  <strong>Country:</strong>{" "}
                  {data.Country?.name || "-"}
                </div>
                <div className="col-md-3">
                  <strong>State:</strong>{" "}
                  {data.State?.name || "-"}
                </div>
                <div className="col-md-3">
                  <strong>City:</strong>{" "}
                  {data.City?.name
                    ? data.City.name
                    : data.OtherCityName
                      ? data.OtherCityName
                      : "-"}
                </div>
                <div className="col-md-3">
                  <strong>PIN / ZIP:</strong>{" "}
                  {data.zipCode || "-"}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Focused Business Area:</strong>{" "}
                  {data.focusedBusinessArea || "-"}
                </div>
              </div>
            </div>

            <hr />

            {/* Step 3: Contact Details */}
            <div className="mb-4">
              <h5 className="mb-3">
                {/* <span className="badge bg-primary me-2">Step 3</span> */}
                Contact Details
              </h5>

              {/* Primary Contact */}
              <div className="mb-4">
                <h6>Primary Contact</h6>
                <div className="row">
                  <div className="col-md-3">
                    <strong>Name:</strong>{" "}
                    {data.primaryContactFullName || "-"}
                  </div>
                  <div className="col-md-3">
                    <strong>Position:</strong>{" "}
                    {data.primaryContactPosition || "-"}
                  </div>
                  <div className="col-md-3">
                    <strong>Mobile:</strong>{" "}
                    {data.primaryContactMobileNumber || "-"}
                  </div>
                  <div className="col-md-3">
                    <strong>Email:</strong>{" "}
                    {data.primaryContactEmail || "-"}
                  </div>
                </div>
              </div>

              {/* Secondary Contact */}
              <div className="mb-4 mt-3">
                <h6>Secondary Contact</h6>
                <div className="row">
                  <div className="col-md-3">
                    <strong>Name:</strong>{" "}
                    {data.secondaryContactFullName || "-"}
                  </div>
                  <div className="col-md-3">
                    <strong>Position:</strong>{" "}
                    {data.secondaryContactPosition || "-"}
                  </div>
                  <div className="col-md-3">
                    <strong>Mobile:</strong>{" "}
                    {data.secondaryContactMobileNumber || "-"}
                  </div>
                  <div className="col-md-3">
                    <strong>Email:</strong>{" "}
                    {data.secondaryContactEmail || "-"}
                  </div>
                </div>
              </div>

              {/* Training Contact */}
              <div className="mb-4 mt-3">
                <h6>Training Department Contact</h6>
                <div className="row">
                  <div className="col-md-3">
                    <strong>Name:</strong>{" "}
                    {data.trainingContactFullName || "-"}
                  </div>
                  <div className="col-md-3">
                    <strong>Position:</strong>{" "}
                    {data.trainingContactPosition || "-"}
                  </div>
                  <div className="col-md-3">
                    <strong>Mobile:</strong>{" "}
                    {data.trainingContactMobileNumber || "-"}
                  </div>
                  <div className="col-md-3">
                    <strong>Email:</strong>{" "}
                    {data.trainingContactEmail || "-"}
                  </div>
                </div>
              </div>

              {/* Marketing Contact */}
              <div className="mb-3 mt-3">
                <h6>Marketing & Communication Contact</h6>
                <div className="row">
                  <div className="col-md-3">
                    <strong>Name:</strong>{" "}
                    {data.marketingContactFullName || "-"}
                  </div>
                  <div className="col-md-3">
                    <strong>Position:</strong>{" "}
                    {data.marketingContactPosition || "-"}
                  </div>
                  <div className="col-md-3">
                    <strong>Mobile:</strong>{" "}
                    {data.marketingContactMobileNumber || "-"}
                  </div>
                  <div className="col-md-3">
                    <strong>Email:</strong>{" "}
                    {data.marketingContactEmail || "-"}
                  </div>
                </div>
              </div>
            </div>

            <hr />

            {/* Step 4: Resolution (only if RESOLVED) */}
            {data.status === "RESOLVED" && (
              <div className="mb-2">
                <h5 className="mb-3">
                  {/* <span className="badge bg-primary me-2">Step 4</span> */}
                  Resolution
                </h5>
                <div className="row mb-2">
                  {/* <div className="col-md-4">
                    <strong>Resolved By:</strong>{" "}
                    {data.resolvedBy?.name || "-"}
                  </div> */}
                  <div>
                    <strong>Resolved Email:</strong>{" "}
                    {data.resolvedBy?.email || "-"}
                  </div>
                  {/* <div className="col-md-4">
                    <strong>Resolved At:</strong>{" "}
                    {formatDateTime(data.resolvedDatetime)}
                  </div> */}
                </div>
                <div className="mb-2">
                  <strong>Resolve Comment:</strong>
                  <div className="border rounded p-2 mt-1 bg-light">
                    {data.resolvedComment || "-"}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default MembershipRequestDetailsPage;
